import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AuthService, UserContext, LoginRequest } from '../services/authService';
import { TranslationCache } from '../i18n';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserContext | null;
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  /** Start impersonering («se som bruker») — kun for utviklerbrukere */
  impersonate: (userId: string) => Promise<void>;
  /** Avslutt impersonering og gjenopprett utviklerbrukerens eget token */
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

// Synkron initialisering av auth-state fra localStorage
// Unngår unødvendig redirect-syklus (/login → /) når token allerede finnes
const initializeAuth = (): { isAuth: boolean; userData: UserContext | null } => {
  const isAuth = AuthService.isAuthenticated();
  if (!isAuth) return { isAuth: false, userData: null };

  const userData = AuthService.getUserDataFromToken();
  if (userData) {
    userData.tenantName = localStorage.getItem('bizclick_tenantName') || undefined;
    userData.defaultWarehouse = localStorage.getItem('bizclick_warehouse') || undefined;
  }
  return { isAuth: true, userData };
};

// Etter token-bytte (impersonering start/slutt) lastes appen på nytt så all
// state (settings, moduletilgang, språk) hentes ferskt for den nye identiteten.
const reloadAfterTokenSwap = () => {
  TranslationCache.clearCache();
  window.location.href = '/';
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [initialState] = useState(() => initializeAuth());
  const [isAuthenticated, setIsAuthenticated] = useState(initialState.isAuth);
  const [user, setUser] = useState<UserContext | null>(initialState.userData);

  const login = async (request: LoginRequest) => {
    const response = await AuthService.login(request);
    AuthService.setToken(response.token);

    // Store tenantName and defaultWarehouse separately (not in JWT token)
    localStorage.setItem('bizclick_tenantName', response.tenantName);
    if (response.defaultWarehouse) {
      localStorage.setItem('bizclick_warehouse', response.defaultWarehouse);
    } else {
      localStorage.removeItem('bizclick_warehouse');
    }

    // isDeveloper/impersonatedBy ligger kun i JWT-claims (ikke i login-responsen)
    const claims = AuthService.getUserDataFromToken();

    const userData = {
      companyNo: response.companyNo,
      customerNo: response.customerNo,
      operatorId: response.operatorId,
      operatorName: response.operatorName,
      operatorGroupId: response.operatorGroupId,
      tenantName: response.tenantName,
      defaultWarehouse: response.defaultWarehouse,
      isDeveloper: claims?.isDeveloper,
      impersonatedBy: claims?.impersonatedBy
    };

    setUser(userData);
    setIsAuthenticated(true);

    // Clear translation cache when user logs in to ensure fresh translations
    TranslationCache.clearCache();
  };

  const impersonate = async (userId: string) => {
    const response = await AuthService.impersonate(userId);

    if (response.defaultWarehouse) {
      localStorage.setItem('bizclick_warehouse', response.defaultWarehouse);
    } else {
      localStorage.removeItem('bizclick_warehouse');
    }

    reloadAfterTokenSwap();
  };

  const stopImpersonation = () => {
    if (!AuthService.endImpersonation()) return;

    // Utviklerbrukeren har ikke eget default warehouse
    localStorage.removeItem('bizclick_warehouse');

    reloadAfterTokenSwap();
  };

  const logout = async () => {
    await AuthService.logout();
    localStorage.removeItem('bizclick_tenantName');
    localStorage.removeItem('bizclick_warehouse');
    localStorage.removeItem('bizclick_wms_language');
    setIsAuthenticated(false);
    setUser(null);

    // Clear translation cache when user logs out
    TranslationCache.clearCache();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, impersonate, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
